import { AnswerObject, AnswerType } from '@models/Survey/Answer'
import { getQuestionChoices } from '@models/Survey/Survey'
import { FlatQuestion, Meta, QuestionGroupMeta, ShowIfRule } from '@models/Survey/Survey'

type MetaLoose = Meta & {
  show_if?: ShowIfRule & { question_key?: string }
  question_group?: QuestionGroupMeta
}

export type QuestionGroupResolved = {
  id: string
  leadId: string
  memberIds: string[]
  stem?: { fr?: string; en?: string }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function readMetaRecord(question: FlatQuestion): Record<string, unknown> | null {
  const meta = question.meta as unknown
  if (typeof meta === 'string') {
    try {
      return asRecord(JSON.parse(meta))
    } catch {
      return null
    }
  }
  return asRecord(meta)
}

/** Read show_if from camelized SPA meta or raw snake_case. */
export function getShowIfRule(question: FlatQuestion): ShowIfRule | undefined {
  const meta = readMetaRecord(question) as MetaLoose | null
  if (!meta) return undefined
  const raw = meta.showIf ?? meta.show_if
  const obj = asRecord(raw)
  if (!obj) return undefined

  const questionKey = String(obj.questionKey ?? obj.question_key ?? '').trim()
  const op = obj.op
  if (!questionKey || (op !== 'eq' && op !== 'neq' && op !== 'in')) {
    return undefined
  }

  if (op === 'in') {
    const values = obj.values
    if (!Array.isArray(values)) return undefined
    return { questionKey, op, values: values.map(String) }
  }

  if (obj.value === undefined || obj.value === null) return undefined
  return { questionKey, op, value: String(obj.value) }
}

/** Read question_group from camelized SPA meta or raw snake_case. */
export function getQuestionGroup(question: FlatQuestion): QuestionGroupMeta | undefined {
  const meta = readMetaRecord(question) as MetaLoose | null
  if (!meta) return undefined
  const raw = meta.questionGroup ?? meta.question_group
  const obj = asRecord(raw)
  if (!obj) return undefined
  const id = String(obj.id ?? '').trim()
  if (!id) return undefined
  const stemRaw = asRecord(obj.stem)
  const stem = stemRaw
    ? {
        fr: stemRaw.fr != null ? String(stemRaw.fr) : undefined,
        en: stemRaw.en != null ? String(stemRaw.en) : undefined,
      }
    : undefined
  return { id, stem }
}

export function resolveQuestionGroups(questionsList: FlatQuestion[]): Map<string, QuestionGroupResolved> {
  const byId = new Map<string, FlatQuestion[]>()
  for (const q of questionsList) {
    const group = getQuestionGroup(q)
    if (!group) continue
    const list = byId.get(group.id) ?? []
    list.push(q)
    byId.set(group.id, list)
  }

  const resolved = new Map<string, QuestionGroupResolved>()
  for (const [id, members] of byId) {
    const sorted = [...members].sort((a, b) => a.order - b.order || a.key.localeCompare(b.key))
    const lead = sorted[0]
    const leadGroup = getQuestionGroup(lead)
    resolved.set(id, {
      id,
      leadId: lead.id,
      memberIds: sorted.map((m) => m.id),
      stem: leadGroup?.stem,
    })
  }
  return resolved
}

type ChoiceLike = { id: string; choiceKey?: string; choice_key?: string; key?: string }

function choiceKeysOf(choice: ChoiceLike): string[] {
  const keys = [choice.id, choice.choiceKey, choice.choice_key, choice.key]
  return keys.filter((k): k is string => k != null && String(k).length > 0).map(String)
}

function answerMatchesExpected(
  answer: AnswerType,
  expected: string,
  sourceQuestion: FlatQuestion,
): boolean {
  const answerStr = typeof answer === 'object' && answer !== null
    ? JSON.stringify(answer)
    : String(answer)

  if (answerStr === expected) return true

  const choices = getQuestionChoices(sourceQuestion) as ChoiceLike[]
  if (!Array.isArray(choices) || choices.length === 0) return false

  const selected = choices.find((c) => String(c.id) === answerStr)
  if (!selected) return false
  return choiceKeysOf(selected).includes(expected)
}

export function evaluateShowIf(
  rule: ShowIfRule | undefined,
  answerObject: AnswerObject,
  questionsList: FlatQuestion[],
): boolean {
  if (!rule) return true

  const source = questionsList.find((q) => q.key === rule.questionKey)
  if (!source) return false

  const answer = answerObject[source.id]
  if (answer === undefined || answer === null || answer === '') {
    return false
  }

  if (rule.op === 'in') {
    const values = rule.values ?? []
    return values.some((v) => answerMatchesExpected(answer, String(v), source))
  }

  const matched = answerMatchesExpected(answer, String(rule.value ?? ''), source)
  return rule.op === 'eq' ? matched : !matched
}

/**
 * V1 visibility:
 * - solo question: own show_if (absent → visible)
 * - group member: lead's show_if only (non-lead show_if ignored)
 */
export function isQuestionVisible(
  question: FlatQuestion,
  answerObject: AnswerObject,
  questionsList: FlatQuestion[],
  groups?: Map<string, QuestionGroupResolved>,
): boolean {
  const resolved = groups ?? resolveQuestionGroups(questionsList)
  const groupMeta = getQuestionGroup(question)

  if (groupMeta) {
    const group = resolved.get(groupMeta.id)
    if (!group) return true
    const lead = questionsList.find((q) => q.id === group.leadId)
    if (!lead) return true
    return evaluateShowIf(getShowIfRule(lead), answerObject, questionsList)
  }

  return evaluateShowIf(getShowIfRule(question), answerObject, questionsList)
}

export function visibleQuestions(
  questionsList: FlatQuestion[],
  answerObject: AnswerObject,
): FlatQuestion[] {
  const groups = resolveQuestionGroups(questionsList)
  return questionsList.filter((q) => isQuestionVisible(q, answerObject, questionsList, groups))
}

export function nextVisibleIndex(
  questionsList: FlatQuestion[],
  fromIndex: number,
  answerObject: AnswerObject,
): number | undefined {
  const groups = resolveQuestionGroups(questionsList)
  for (let i = fromIndex + 1; i < questionsList.length; i += 1) {
    if (isQuestionVisible(questionsList[i], answerObject, questionsList, groups)) {
      return i
    }
  }
  return undefined
}

export function prevVisibleIndex(
  questionsList: FlatQuestion[],
  fromIndex: number,
  answerObject: AnswerObject,
): number | undefined {
  const groups = resolveQuestionGroups(questionsList)
  for (let i = fromIndex - 1; i >= 0; i -= 1) {
    if (isQuestionVisible(questionsList[i], answerObject, questionsList, groups)) {
      return i
    }
  }
  return undefined
}

export function visibleCount(questionsList: FlatQuestion[], answerObject: AnswerObject): number {
  return visibleQuestions(questionsList, answerObject).length
}

/** 1-based position among currently visible questions. */
export function visiblePosition(
  questionsList: FlatQuestion[],
  index: number,
  answerObject: AnswerObject,
): number {
  if (index < 0 || index >= questionsList.length) return 0
  const groups = resolveQuestionGroups(questionsList)
  let pos = 0
  for (let i = 0; i <= index; i += 1) {
    if (isQuestionVisible(questionsList[i], answerObject, questionsList, groups)) {
      pos += 1
    }
  }
  return pos
}

export function questionGroupStem(
  question: FlatQuestion,
  language: string,
  questionsList?: FlatQuestion[],
): string | undefined {
  const own = getQuestionGroup(question)
  if (!own) return undefined

  let stem = own.stem
  if (questionsList?.length) {
    const groups = resolveQuestionGroups(questionsList)
    const resolved = groups.get(own.id)
    if (resolved?.stem) stem = resolved.stem
  }

  if (!stem) return undefined
  const lang = language.toLowerCase().startsWith('en') ? 'en' : 'fr'
  const text = (stem[lang] ?? stem.fr ?? stem.en ?? '').trim()
  return text || undefined
}
